import { Box, Button, HStack, VStack, Stack, useSteps, Spinner } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { getBrowserTimezoneId, updateFormWithUserData, utcMinutesToHHMM } from '@util/helpers';

import { useStyledToast, useOnboardingSteps } from '@util/hooks';
import { useUserStore } from '@store/app.store';
import { usePatchPerson, usePeople } from '@services/query-services/people.query-service';
import OnboardingComplete from '@components/onboarding/onboarding-complete.component';
import StepContentsContainer from '@components/onboarding/step-contents-container.component';
import { useSearchParams } from 'react-router-dom';
import OnboardingHeader from '@components/onboarding/onboarding-header.component';

export default function OnboardingPage() {
  const [query, setQuery] = useSearchParams();
  const stepFromUrl = query.get('step');

  const [isLoading, setIsLoading] = useState(false);
  const [isNextButtonDisabled, setIsNextButtonDisabled] = useState(false);
  const user = useUserStore((state) => state.user);
  const setUser = useUserStore((state) => state.setUser);
  const tenant = useUserStore((state) => state.tenant);
  const hasCompletedOnboarding = useUserStore((state) => state.hasCompletedOnboarding());

  const { mutateAsync: patchPerson } = usePatchPerson();
  const { data: people } = usePeople(
    {
      active: true,
      pageSize: 25
    },
    { includeTotal: true }
  );

  const toast = useStyledToast();

  /** @type {UseForm<OnboardingFormValues>} */
  const form = useForm({
    defaultValues: {
      context: '',
      tenant_context: '',
      title: '',
      first_name: '',
      last_name: '',
      department: '',
      morning_digest_enabled: true,
      morning_digest_time: utcMinutesToHHMM(0),
      evening_digest_enabled: true,
      evening_digest_time: utcMinutesToHHMM(960),
      time_zone_id: getBrowserTimezoneId(),
      users: people?.data?.map((p) => {
        return { email: p.email, type: p.type, id: p.id, isExisting: true };
      }),
      tenant: {
        name: tenant?.name || '',
        id: tenant?.id || ''
      },
      auto_schedule: false
    }
  });

  const steps = useOnboardingSteps({
    form,
    setIsNextButtonDisabled
  });

  useEffect(() => {
    updateFormWithUserData(form, user, tenant, people);
  }, [user, tenant]);

  const handleNextStep = async () => {
    setIsLoading(true);
    const onStepSubmit = steps[activeStep]?.onStepSubmit;
    if (onStepSubmit) {
      try {
        await onStepSubmit();
        goToNext();
      } catch (error) {
        console.error('Error submitting step:', error);
      }
    } else {
      goToNext();
    }
    setIsLoading(false);
  };

  const handlePreviousStep = () => {
    setIsNextButtonDisabled(false);
    goToPrevious();
  };

  // Calculate initial step from URL
  const getInitialStepIndex = () => {
    if (!stepFromUrl) return 0;
    const stepNumber = parseInt(stepFromUrl, 10);
    if (isNaN(stepNumber)) return 0;
    const stepIndex = stepNumber - 1;
    if (stepIndex < 0 || stepIndex >= steps.length) return 0;
    return stepIndex;
  };

  const { activeStep, goToPrevious, goToNext, setActiveStep } = useSteps({
    index: getInitialStepIndex(),
    count: steps.length
  });

  // Sync URL with activeStep changes
  useEffect(() => {
    const newStepParam = (activeStep + 1).toString();
    if (stepFromUrl !== newStepParam) {
      setQuery({ step: newStepParam });
    }
  }, [activeStep, stepFromUrl, setQuery]);

  // Handle URL step changes (for direct navigation)
  useEffect(() => {
    const stepIndex = getInitialStepIndex();
    if (stepIndex !== activeStep) {
      setActiveStep(stepIndex);
    }
  }, [stepFromUrl]);

  const isLastStep = activeStep === steps.length - 1;

  /** @param {OnboardingFormValues} values */
  const onSubmit = async () => {
    const tags = user?.tags ? [...user.tags, 'onboarding-completed'] : ['onboarding-completed'];
    const updatedUser = await patchPerson(
      {
        id: user?.id,
        tags
      },
      {
        onError: (e) => {
          toast({
            status: 'error',
            title: 'error patching user',
            description: e?.message
          });
        }
      }
    );
    setUser(updatedUser);
    setIsLoading(false);
  };

  const handleLastStep = async () => {
    setIsLoading(true);
    const onStepSubmit = steps[activeStep]?.onStepSubmit;
    if (onStepSubmit) {
      try {
        await onStepSubmit();
      } catch (error) {
        console.error('Error submitting step:', error);
        setIsLoading(false);

        return;
      }
    }

    try {
      await form.handleSubmit(onSubmit)();
    } catch (e) {
      console.error('Error submitting form:', e);
      setIsLoading(false);
    }
    setIsLoading(false);
  };

  return (
    <Box
      w='full'
      h='full'
      bgColor='#F5F9FC'
    >
      <VStack
        mx='auto'
        maxW='1440px'
        w='full'
        h='full'
      >
        <Box
          w='full'
          minH={32}
        >
          {!hasCompletedOnboarding && (
            <OnboardingHeader
              steps={steps}
              activeStep={activeStep}
            />
          )}
        </Box>
        <Stack
          onSubmit={(e) => e.preventDefault()}
          as='form'
          maxW='800px'
          pb={40}
          w='full'
          h='full'
          justify='center'
          gap={10}
        >
          {hasCompletedOnboarding ? (
            <OnboardingComplete firstName={user?.first_name} />
          ) : (
            <StepContentsContainer title={steps[activeStep].title}>
              {steps[activeStep].content}
            </StepContentsContainer>
          )}
          {!hasCompletedOnboarding && (
            <HStack
              px={{ base: 4, md: 0 }}
              w='full'
              gap={10}
            >
              <Button
                type='button'
                flex={1}
                isDisabled={activeStep === 0}
                variant='reskinOutline'
                onClick={handlePreviousStep}
              >
                Back
              </Button>
              {isLastStep ? (
                <Button
                  type='button'
                  onClick={handleLastStep}
                  isLoading={isLoading}
                  flex={1}
                >
                  Complete
                </Button>
              ) : (
                <Button
                  type='button'
                  flex={1}
                  onClick={handleNextStep}
                  isDisabled={isNextButtonDisabled || isLoading}
                >
                  {isLoading && (
                    <Spinner
                      size='sm'
                      mr={2}
                    />
                  )}
                  Next
                </Button>
              )}
            </HStack>
          )}
        </Stack>
      </VStack>
    </Box>
  );
}
